/**
 * 統計画面 - localStorage に保存された統計データを表示
 */

import { useState, useEffect } from 'react';
import { GAME_REGISTRY } from '../config/gameRegistry';
import { loadStatistics, type StatisticsData, type MapStatistics } from '../utils/storage';
import { useTranslations } from '../i18n';
import { BASE_PATH } from '../config';

const MAP_OPTIONS = Object.entries(GAME_REGISTRY)
  .filter(([_, entry]) => !entry.isWIP)
  .map(([id, entry]) => ({
    id,
    apiMapType: entry.apiMapType,
    displayName: entry.displayName,
  }));

const SEATTLE_MAP_TYPE = 'seattle_metro_solo';

export function StatisticsScreen() {
  const t = useTranslations();
  const [selectedApiMapType, setSelectedApiMapType] = useState(
    MAP_OPTIONS[0]?.apiMapType || 'barbados',
  );
  const [statsData, setStatsData] = useState<StatisticsData>({});

  useEffect(() => {
    setStatsData(loadStatistics());
  }, []);

  const stats: MapStatistics | undefined = statsData[selectedApiMapType];
  const hasData = stats && stats.playCount > 0;
  const hasClearData = stats && stats.clearCount > 0;

  const avg = (sum: number | undefined, count: number): string => {
    if (!sum || count === 0) return '-';
    return (sum / count).toFixed(1);
  };

  const isSeattle = selectedApiMapType === SEATTLE_MAP_TYPE;

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-6">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl md:text-2xl font-bold">{t.statistics.title}</h1>
          <button
            className="btn-secondary px-3 py-1.5 text-sm"
            onClick={() => { window.location.href = BASE_PATH; }}
          >
            {t.statistics.back}
          </button>
        </div>

        {/* Storage notice */}
        <div className="bg-muted/30 rounded-lg p-3 mb-4 text-xs text-muted-foreground">
          {t.statistics.storageNotice}
        </div>

        {/* Map Selector */}
        <select
          className="w-full px-3 py-2 text-sm rounded border border-border bg-background text-foreground mb-4"
          value={selectedApiMapType}
          onChange={(e) => setSelectedApiMapType(e.target.value)}
        >
          {MAP_OPTIONS.map((m) => (
            <option key={m.id} value={m.apiMapType}>
              {m.displayName}
            </option>
          ))}
        </select>

        {!hasData ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {t.statistics.noData}
          </div>
        ) : (
          <div className="space-y-2">
            {/* プレイ回数セクション */}
            <StatRow label={t.statistics.playCount} value={stats.playCount} />
            <StatRow label={t.statistics.clearCount} value={stats.clearCount} />
            <StatRow label={t.statistics.noUndoClearCount} value={stats.noUndoClearCount} />
            <StatRow
              label={t.statistics.bestScore}
              value={stats.bestScore === -Infinity ? '-' : stats.bestScore}
              highlight
            />

            {/* 平均セクション（クリアデータがある場合のみ） */}
            {hasClearData && (
              <>
                <div className="border-t border-border my-3" />
                <StatRow label={t.statistics.avgCash} value={avg(stats.sumCash, stats.clearCount)} />
                <StatRow label={t.statistics.avgShares} value={avg(stats.sumShares, stats.clearCount)} />
                <StatRow label={t.statistics.avgLoco} value={avg(stats.sumLoco, stats.clearCount)} />
                <StatRow label={t.statistics.avgLinks} value={avg(stats.sumLinks, stats.clearCount)} />

                {isSeattle && (
                  <>
                    <div className="border-t border-border my-3" />
                    <StatRow label={t.statistics.avgCargoLv} value={avg(stats.sumCargoLv, stats.clearCount)} />
                    <StatRow label={t.statistics.avgDelivered} value={avg(stats.sumDelivered, stats.clearCount)} />
                    <StatRow label={t.statistics.avgRemaining} value={avg(stats.sumRemaining, stats.clearCount)} />
                    <StatRow label={t.statistics.avgUnconnected} value={avg(stats.sumUnconnected, stats.clearCount)} />
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatRow({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center rounded-lg border border-border bg-white p-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-medium ${highlight ? 'text-primary' : ''}`}>{value}</span>
    </div>
  );
}
